import React, { EventHandler } from 'react';
import PropTypes from 'prop-types';
import Accordion from '../Accordion/Accordion';
import AccordionItem from '../Accordion/AccordionItem';
import Address from '../Address/Address';
import { format, parseISO } from 'date-fns';
import useMode from '../../hooks/useMode';
import { Box, Flex, Text } from 'rebass';
import BackflowTestReportEdit from '../BackflowTest/BackflowTestReportEdit';
import TesterPicker, { Tester } from '../Tester/TesterPicker';
import { JobFormDataInput } from '@ecosconnect/core/src/types/jobFormData';
import BackflowTestAssemblyEdit from '../BackflowTest/assembly/BackflowTestAssemblyEdit';
import GaugePicker from '../Gauge/GaugePicker';
import BackflowTestAssembly from '../BackflowTest/assembly/BackflowTestAssembly';
import BackflowTestReport from '../BackflowTest/BackflowTestReport';
import JobPhotosEdit from './JobPhotosEdit';
import FilePreviews from '../File/FilePreviews';
import CredentialPicker from '../Credential/CredentialPicker';
import { isTestable } from '@ecosconnect/core/src/helpers/backflow/helpers';
import { SetFormValue, SetFormValues } from './JobThirdPane';
import useSessionInfo from '../../hooks/useSessionInfo';
import { useMutation } from '@apollo/client';
import { GrowyTextarea } from '../Form/GrowyTextarea';
import { gql } from '@/modes/individual/gql';
import RequirePerms from '@/components/RequirePerms';
import {
  AssemblyInfo,
  BackflowTest,
  Customer,
  Job,
  VendorProfile,
} from './queries';

const REMOVE_JOB_ATTACHMENT = gql(`
  mutation RemoveJobAttachment($jobId: ID!, $attachmentId: ID!) {
    removeJobAttachment(jobId: $jobId, attachmentId: $attachmentId)
  }
`);

type JobDetailsProps = {
  action: 'EDIT' | 'VIEW';
  customer: Customer;
  vendorProfile?: VendorProfile;
  backflowTest?: BackflowTest;
  job: Job;
  formData?: JobFormDataInput;
  formAssembly?: AssemblyInfo;
  onTesterChange?: (tester: Tester | null) => void;
  onNestedTesterChange?: (tester: Tester | null) => void;
  onFormInputChange: EventHandler<any>;
  setFormValue: SetFormValue;
  setFormValues: SetFormValues;
  refetch?: () => Promise<unknown>;
};

const JobDetail: React.FC<JobDetailsProps> = ({
  action,
  customer,
  vendorProfile,
  backflowTest,
  job,
  formData,
  formAssembly,
  onTesterChange,
  onNestedTesterChange,
  onFormInputChange,
  setFormValue,
  setFormValues,
  refetch,
}) => {
  const mode = useMode();

  const backflowAssembly = backflowTest?.backflowAssembly!;

  const { waterService } = backflowAssembly ?? {};

  const originalBackflowAssembly: AssemblyInfo | undefined =
    backflowTest?.originalBackflowAssembly || backflowAssembly;

  const { me } = useSessionInfo();

  const assignedToProfile: VendorProfile | undefined =
    mode === 'company' || mode === 'water-supplier'
      ? vendorProfile
      : mode === 'individual'
        ? me
        : undefined;
  const testerProfileId =
    backflowTest?.testerId ||
    (assignedToProfile?.type === 'INDIVIDUAL'
      ? assignedToProfile?.id
      : undefined);
  const canAssignNestedTester =
    mode === 'water-supplier' && vendorProfile?.type === 'COMPANY';
  const isAssignedToIndividual =
    assignedToProfile?.type === 'INDIVIDUAL' ||
    (mode === 'water-supplier' && backflowTest?.testerId);

  const [removeJobAttachment] = useMutation<
    { removeJobAttachment: boolean },
    { jobId: string; attachmentId: string }
  >(REMOVE_JOB_ATTACHMENT);

  return (
    <>
      <Flex flexDirection={['column', 'column', 'row']}>
        <Box width={['100%', '100%', '50%']} flexGrow={1} flexShrink={1}>
          {mode !== 'individual' && (
            <Accordion title="Customer">
              {customer && (
                <>
                  <AccordionItem
                    kind="link"
                    label="Name"
                    value={customer.profile.name}
                    href={`/${mode}/customers/${customer.id}`}
                  />
                  {customer.profile.phones?.length > 0 && (
                    <AccordionItem
                      label="Phone"
                      value={customer.profile.phones[0].value}
                    />
                  )}
                  {customer.profile.emails?.length > 0 && (
                    <AccordionItem
                      label="Email"
                      value={customer.profile.emails[0].value}
                    />
                  )}
                  {customer.notes && (
                    <AccordionItem label="Notes" value={customer.notes} />
                  )}
                </>
              )}
              <AccordionItem
                label="Address"
                value={<Address address={job.address} />}
              />
            </Accordion>
          )}
          {job.siteContact &&
            (mode === 'individual' ||
              customer.profile.id !== job.siteContact.id) && (
              <Accordion title="Site Contact">
                <AccordionItem label="Name" value={job.siteContact.name} />
                {job.siteContact.phone && (
                  <AccordionItem label="Phone" value={job.siteContact.phone} />
                )}
                {job.siteContact.email && (
                  <AccordionItem label="Email" value={job.siteContact.email} />
                )}
                <AccordionItem
                  label="Address"
                  value={<Address address={job.address} />}
                />
                {job.siteContact.notes && (
                  <AccordionItem label="Notes" value={job.siteContact.notes} />
                )}
              </Accordion>
            )}

          {waterService && (
            <Accordion title="Water Service">
              {waterService.siteNumber && (
                <AccordionItem
                  label="Service #"
                  value={waterService.siteNumber}
                />
              )}
              {waterService.meterNumber && (
                <AccordionItem
                  label="Meter #"
                  value={waterService.meterNumber}
                />
              )}
              {waterService.pin && (
                <AccordionItem label="TRAC" value={waterService.pin} />
              )}
            </Accordion>
          )}

          <Accordion
            title={
              mode === 'company' || mode === 'water-supplier'
                ? 'Tester'
                : 'Credentials'
            }
          >
            {action === 'EDIT' && (
              <>
                {(mode === 'company' || mode === 'water-supplier') && (
                  <RequirePerms assignments="UPDATE">
                    <AccordionItem
                      label="Assigned To"
                      alignItems="center"
                      as="label"
                      value={
                        <TesterPicker
                          testerProfileId={vendorProfile?.id}
                          where={{ status: 'ACTIVE' }}
                          onChange={onTesterChange}
                        />
                      }
                    />
                  </RequirePerms>
                )}
                {canAssignNestedTester && (
                  <RequirePerms assignments="UPDATE">
                    <AccordionItem
                      label="Tester"
                      alignItems="center"
                      as="label"
                      value={
                        <TesterPicker
                          testerProfileId={backflowTest?.testerId}
                          companyProfileId={vendorProfile?.id}
                          onChange={onNestedTesterChange}
                        />
                      }
                    />
                  </RequirePerms>
                )}
                {testerProfileId && isAssignedToIndividual && formData && (
                  <>
                    <AccordionItem
                      label="Gauge"
                      alignItems="center"
                      as="label"
                      value={
                        <GaugePicker
                          key={testerProfileId}
                          testerProfileId={testerProfileId}
                          companyProfileId={
                            canAssignNestedTester
                              ? vendorProfile?.id
                              : undefined
                          }
                          gaugeId={formData.gaugeId}
                          onChange={(gauge) => {
                            setFormValues({
                              gaugeId: gauge?.id,
                              gaugeNumber: gauge?.gaugeNumber,
                              gaugeCalibrated: gauge?.calibrationDate,
                            });
                          }}
                          allowCreate
                        />
                      }
                    />
                    <AccordionItem
                      label="Cert #"
                      alignItems="center"
                      as="label"
                      value={
                        <CredentialPicker
                          key={vendorProfile?.id}
                          type="BACKFLOW"
                          testerProfileId={testerProfileId}
                          companyProfileId={
                            canAssignNestedTester
                              ? vendorProfile?.id
                              : undefined
                          }
                          certificationNumber={formData.certificationNumber}
                          // date={formData.tests?.[0]?.testDate}
                          onChange={(credential) => {
                            setFormValues({
                              certificationNumber: credential?.value,
                              certificationState: credential?.state,
                            });
                          }}
                          allowCreate
                        />
                      }
                    />
                  </>
                )}
                {testerProfileId &&
                  !isAssignedToIndividual &&
                  backflowTest?.testerName &&
                  formData && (
                    <>
                      {backflowTest.testerName && (
                        <AccordionItem
                          label="Tester"
                          value={backflowTest.testerName}
                        />
                      )}
                      <AccordionItem
                        label="Gauge"
                        value={formData.gaugeNumber}
                      />
                      {formData.gaugeCalibrated && (
                        <AccordionItem
                          label="Gauge Calibrated"
                          value={format(
                            parseISO(formData.gaugeCalibrated),
                            'M/d/yyyy'
                          )}
                        />
                      )}
                    </>
                  )}
              </>
            )}
            {action === 'VIEW' && formData && vendorProfile && (
              <>
                <AccordionItem label="Name" value={vendorProfile.name} />
                <AccordionItem label="Gauge" value={formData.gaugeNumber} />
                {formData.gaugeCalibrated && (
                  <AccordionItem
                    label="Gauge Calibrated"
                    value={format(
                      parseISO(formData.gaugeCalibrated),
                      'M/d/yyyy'
                    )}
                  />
                )}
              </>
            )}
          </Accordion>
          {formData && (
            <>
              {action === 'VIEW' && (
                <BackflowTestAssembly
                  key={job.id}
                  originalBackflowAssembly={originalBackflowAssembly}
                  backflowAssemblyId={backflowAssembly?.id}
                  formAssembly={formAssembly}
                  formData={formData}
                />
              )}
              {action === 'EDIT' && (
                <BackflowTestAssemblyEdit
                  key={job.id}
                  originalBackflowAssembly={originalBackflowAssembly}
                  backflowAssemblyId={backflowAssembly?.id}
                  formAssembly={formAssembly}
                  formData={formData}
                  onFormInputChange={onFormInputChange}
                  setFormValue={setFormValue}
                  setFormValues={setFormValues}
                />
              )}
            </>
          )}
        </Box>
        <Box width={['100%', '100%', '50%']} flexGrow={1} flexShrink={1}>
          {action === 'EDIT' && backflowTest && formData && (
            <BackflowTestReportEdit
              formData={formData}
              assemblyType={formAssembly?.type ?? undefined}
              rpCheck2Type={backflowTest.waterSupplier?.rpCheck2Type ?? 'USC10'}
              onFormInputChange={onFormInputChange}
              setFormValue={setFormValue}
            />
          )}
          {action === 'VIEW' && backflowTest && formData && (
            <BackflowTestReport
              formData={formData}
              assemblyType={formAssembly?.type ?? undefined}
              rpCheck2Type={backflowTest.waterSupplier?.rpCheck2Type ?? 'USC10'}
            />
          )}

          {(action === 'EDIT' ||
            job.attachments.length > 0 ||
            formData?.comments) && (
            <Accordion
              title="Photos and Comments"
              icon={
                action === 'EDIT' ? (
                  <JobPhotosEdit onPhotosAdded={refetch} jobId={job.id} />
                ) : undefined
              }
            >
              <FilePreviews
                files={job.attachments}
                onDelete={
                  action === 'EDIT'
                    ? (file) => {
                        if (
                          !confirm(
                            'Are you sure you want to delete this photo?'
                          )
                        ) {
                          return;
                        }
                        removeJobAttachment({
                          variables: {
                            jobId: job.id,
                            attachmentId: file.id,
                          },
                          onCompleted: refetch,
                        });
                      }
                    : undefined
                }
                mb={action === 'EDIT' ? 2 : undefined}
              />

              {action === 'VIEW' && !!formData?.comments && (
                <>
                  <AccordionItem label="Comments" value=" " />
                  <Text variant="body2">{formData.comments}</Text>
                </>
              )}
              {action === 'EDIT' && (
                <GrowyTextarea
                  id="comments"
                  required={formData && !isTestable(formData)}
                  placeholder="Enter comments"
                  onChange={onFormInputChange}
                  name="comments"
                  value={formData?.comments}
                  focusedHeight={98}
                  hasValueHeight={98}
                  height={66}
                  maxCharacters={255}
                />
              )}
            </Accordion>
          )}
        </Box>
      </Flex>
    </>
  );
};

JobDetail.propTypes = {
  backflowTest: PropTypes.any,
};

export default JobDetail;
